import { formatDate } from '@angular/common';
import { Component, ElementRef, HostBinding, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { combineLatest, merge, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { DayTimeOptions } from '../../../../classes/day-time-options';
import { PlanningHasEntity } from '../../../../classes/planning-has-entity.class';
import { DateSmallAdapter } from '../../../../date-adapters/date-small-adapter';
import { EntityUnavailableService } from '../../../../services/entities/entity-unavailable.service';
import { EntityTypeService } from '../../../../services/entity-type.service';
import { PlanningService } from '../../../../services/planning/planning.service';
import { Utils } from '../../../../utils.class';
import { InputComponent } from '../input/input.component';

const NO_EMIT = { emitEvent: false } as const;

@Component({
    selector: 'app-date-time-input',
    standalone: false,
    templateUrl: './date-time-input.component.html',
    styleUrl: './date-time-input.component.scss',
    providers: [
        {
            provide: DateAdapter,
            useClass: DateSmallAdapter,
        },
    ],
})
export class DateTimeInputComponent extends InputComponent implements OnInit {
    private entityUnavailableService = inject(EntityUnavailableService);
    private planningService = inject(PlanningService);
    private entitytypeService = inject(EntityTypeService);

    avCheckSubscriptions: Subscription;
    subscriptions = new Subscription();

    times: DayTimeOptions[];
    @HostBinding('class.show-date') showDate = true;

    datetimeFg: FormGroup<{
        date: FormControl<Date | null>;
        time: FormControl<number | string | Date | null>;
    }>;

    @ViewChild('timeTableInput') timeTableInput: ElementRef;

    ngOnInit() {
        this.showDate = !this.field.dataFilter?.includes('isEndDate');
        const initialTime = this.fc.value
            ? this.tableStyle
                ? formatDate(this.fc.value, 'HH:mm', 'nl')
                : new Date(this.fc.value).getTime()
            : null;

        this.datetimeFg = new FormGroup({
            date: new FormControl(this.fc.value),
            time: new FormControl<number | string | Date | null>(initialTime, {
                updateOn: this.tableStyle ? 'blur' : 'change',
            }),
        });

        for (const rule of this.field.requiredRules) {
            this.entitytypeService.setupRule(
                this.datetimeFg.controls.date,
                this.field,
                rule,
                this.formData.form,
                ['planning', 'planningData'],
                this.subscriptions
            );
            this.entitytypeService.setupRule(
                this.datetimeFg.controls.time,
                this.field,
                rule,
                this.formData.form,
                ['planning', 'planningData'],
                this.subscriptions
            );
        }

        if (!this.tableStyle) {
            this.setTimesSubscription();
        }

        this.subscriptions.add(
            this.fc.valueChanges.subscribe(value => {
                this.datetimeFg.controls.date.markAsTouched(NO_EMIT);
                this.datetimeFg.controls.date.markAsDirty(NO_EMIT);
                this.datetimeFg.controls.date.updateValueAndValidity(NO_EMIT);

                this.datetimeFg.controls.time.markAsTouched(NO_EMIT);
                this.datetimeFg.controls.time.markAsDirty(NO_EMIT);
                this.datetimeFg.controls.time.updateValueAndValidity(NO_EMIT);

                const timeValue = value
                    ? this.tableStyle
                        ? formatDate(value, 'HH:mm', 'nl')
                        : new Date(value).getTime()
                    : null;
                this.datetimeFg.setValue({ date: value, time: timeValue }, NO_EMIT);

                if (!this.tableStyle) {
                    this.setTimesSubscription();
                }
            })
        );

        this.subscriptions.add(
            this.datetimeFg.controls.date.valueChanges.subscribe(value => {
                if (this.tableStyle) {
                    return;
                }

                const time = new Date(this.datetimeFg.controls.time.value);
                const selectedDate = new Date(value);

                time.setFullYear(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

                this.datetimeFg.controls.time.setValue(time.getTime(), NO_EMIT);

                if (!Utils.matchesDate(this.fc.value, value)) {
                    this.fc.markAsDirty(NO_EMIT);
                }

                this.fc.setValue(time);

                this.setTimesSubscription();

                this.fc.markAsTouched();
            })
        );

        this.subscriptions.add(
            this.datetimeFg.controls.time.valueChanges.subscribe((value: Date | string) => {
                if (this.tableStyle) {
                    return;
                }

                const date = value ? new Date(value) : null;

                if (Utils.getTimeOrNull(this.fc.value) !== Utils.getTimeOrNull(date)) {
                    this.fc.markAsDirty(NO_EMIT);
                }

                this.fc.setValue(date);

                this.datetimeFg.controls.date.setValue(value ? new Date(value) : null, NO_EMIT);

                this.fc.markAsTouched();
            })
        );

        if (this.tableStyle) {
            this.subscriptions.add(
                this.datetimeFg.controls.time.valueChanges.subscribe(value => {
                    handleTimeInput(value as string, this.datetimeFg.controls.time);
                })
            );

            this.subscriptions.add(
                merge(this.datetimeFg.controls.date.valueChanges, this.datetimeFg.controls.time.valueChanges).subscribe(
                    () => {
                        const timeStr = this.datetimeFg.controls.time.value as string;
                        const dateVal = this.datetimeFg.controls.date.value;
                        if (timeStr && dateVal) {
                            const timeParts = timeStr.split(':');
                            if (timeParts.length === 2) {
                                const date = new Date(dateVal);
                                date.setHours(+timeParts[0]);
                                date.setMinutes(+timeParts[1]);
                                if (Utils.getTimeOrNull(this.fc.value) !== date.getTime()) {
                                    this.fc.markAsDirty(NO_EMIT);
                                }
                                this.fc.setValue(date);
                                this.fc.markAsTouched();
                            }
                        } else {
                            this.fc.setValue(null);
                            // fc.valueChanges wipes date; restore so a time can still be entered
                            this.datetimeFg.controls.date.setValue(dateVal, NO_EMIT);
                            this.fc.markAsTouched();
                        }
                    }
                )
            );
        }

        this.subscriptions.add(
            this.fc.statusChanges.pipe(startWith(this.fc.status)).subscribe(() => {
                this.fc.disabled ? this.datetimeFg.disable(NO_EMIT) : this.datetimeFg.enable(NO_EMIT);
            })
        );
    }

    setTimesSubscription() {
        if (this.avCheckSubscriptions?.unsubscribe) {
            this.avCheckSubscriptions.unsubscribe();
        }

        const mainPlanning = this.formData.orginal?.mainPlanning ?? new PlanningHasEntity();

        let fromDate = new Date(this.datetimeFg.value.date ?? Utils.formatDate(new Date()));

        const isEndDate = this.field.dataFilter?.includes('isEndDate');
        if (isEndDate) {
            fromDate = new Date(this.fc.parent.get('begindate').value ?? fromDate);
            fromDate.setMinutes(fromDate.getMinutes() + 30);
        } else {
            fromDate.setHours(0);
        }

        const toDate = new Date(fromDate);

        toDate.setUTCHours(toDate.getUTCHours() + (isEndDate ? 20 : 47));

        const fromDateUnavailable = new Date(fromDate);

        fromDateUnavailable.setDate(fromDateUnavailable.getDate() - 14);

        const planning$ = this.planningService.getFilteredList(fromDateUnavailable, toDate);
        const unavailable$ = this.entityUnavailableService.getFilteredList(fromDateUnavailable, toDate);

        this.avCheckSubscriptions = new Subscription();

        this.avCheckSubscriptions.add(
            combineLatest([unavailable$, planning$]).subscribe(([unavailableList, planningList]) => {
                this.times = Utils.genTimesMapOneEntity(
                    fromDate,
                    toDate,
                    mainPlanning,
                    mainPlanning.planning_id,
                    planningList,
                    unavailableList,
                    isEndDate
                );
            })
        );
    }

    ngOnDestroy() {
        this.avCheckSubscriptions?.unsubscribe();

        super.ngOnDestroy();
    }
}

function handleTimeInput(value: string, fc: FormControl<number | string | Date | null>) {
    if (!value) {
        return;
    }
    let newValue = value.replace('.', ':');
    const splitted = newValue.split(':'),
        hour = +splitted[0],
        minute = +splitted[1];
    if (splitted.length === 2 && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        newValue = '' + hour + ':' + (minute < 10 ? '0' : '') + minute;
    } else {
        const testHour = +newValue;
        if (newValue.length <= 2 && testHour >= 0 && testHour <= 23) {
            newValue = newValue + ':00';
        } else if (newValue.length > 2) {
            for (let place = 2; place >= 1; place--) {
                const tempValue = [newValue.slice(0, place), newValue.slice(place)].join(':');
                const tempSplitted = tempValue.split(':'),
                    tempHour = +tempSplitted[0],
                    tempMinute = +tempSplitted[1];
                if (
                    tempSplitted.length === 2 &&
                    tempHour >= 0 &&
                    tempHour <= 23 &&
                    tempMinute >= 0 &&
                    tempMinute <= 59
                ) {
                    newValue = tempValue;
                    break;
                }
            }
        }
    }
    // Round to 3 minutes
    const newValueSplit = newValue.split(':') as any[];
    if (newValueSplit.length > 1) {
        while (+newValueSplit[1] % 3 !== 0) {
            newValueSplit[1] = +newValueSplit[1] - 1;
        }
    }
    const correctedNewValue = newValueSplit.join(':');
    if (value !== correctedNewValue) {
        fc.setValue(correctedNewValue);
    }
}
